import React from 'react';
import classNames from 'clsx';
import CheckboxMarkedOutline from 'mdi-material-ui/CheckboxMarkedOutline';
import CloseBoxOutline from 'mdi-material-ui/CloseBoxOutline';
import ScaleBalance from 'mdi-material-ui/ScaleBalance';
import CheckBox from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlank from '@material-ui/icons/CheckBoxOutlineBlank';
import isNil from 'lodash-es/isNil';

import { SelectBoxMode, SelectBoxProps } from './SelectBox.interfaces';
import { useStyles } from './SelectBox.styles';

export default function SelectBox(props: SelectBoxProps) {
  const classes = useStyles(props);
  const {
    checked,
    checkedTitle,
    checkedTitleSuffix,
    className,
    dataCy,
    mode,
    onChange,
    onCheckedClick,
    readonly,
    title,
    tabIndex,
    role,
  } = props;

  const Icon = () => {
    switch (mode) {
      case SelectBoxMode.Icon:
        return checked ? <CheckboxMarkedOutline /> : <CloseBoxOutline />;
      case SelectBoxMode.Icon2:
        return checked ? <CheckBox /> : <CheckBoxOutlineBlank />;
      case SelectBoxMode.CompareIcon:
        return checked ? <CheckBox /> : <ScaleBalance />;
      default:
        return null;
    }
  };

  const getCheckedClass = () => (checked ? classes.checked : classes.unchecked);

  const getCheckedLink = () => {
    return (
      onCheckedClick && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            if (onCheckedClick) {
              onCheckedClick();
            }
          }}
        >
          {getTitle()}
        </span>
      )
    );
  };

  const getTitle = () => {
    const cTitle = checkedTitleSuffix ? (
      <>
        {checkedTitle}{' '}
        <span className={classes.checkedTitleSuffix}>{checkedTitleSuffix}</span>
      </>
    ) : (
      checkedTitle
    );

    return checked && checkedTitle ? cTitle : title;
  };

  const handleClick = () => {
    if (readonly) {
      return;
    }

    if (onChange) {
      const newState = !checked;
      onChange(newState);
    }
  };

  return (
    <div
      {...(!isNil(tabIndex) && { tabIndex })}
      {...(role && { role })}
      onClick={handleClick}
      className={classNames(
        classes.root,
        'focusVisibleOutline',
        {
          [classes.iconMode]: mode === SelectBoxMode.Icon,
          [classes.readonly]: readonly,
        },
        getCheckedClass(),
        className
      )}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          checked
            ? onCheckedClick && onCheckedClick()
            : onChange && onChange(!checked);
        }
      }}
      data-cy={dataCy}
    >
      <div
        {...(mode === SelectBoxMode.CompareIcon && checked
          ? {
              tabIndex: 0,
              onKeyDown: (e) => {
                if (
                  e.target === e.currentTarget &&
                  (e.key === 'Enter' || e.key === ' ')
                ) {
                  e.preventDefault();
                  handleClick();
                }
              },
            }
          : {})}
        className={classNames(classes.checkArea, getCheckedClass(), {
          focusVisibleOutline: mode === SelectBoxMode.CompareIcon && checked,
        })}
      >
        <Icon />
      </div>
      <span>{checked && onCheckedClick ? getCheckedLink() : getTitle()}</span>
    </div>
  );
}
